import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Send, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, BRANCH_COLORS, SEVERITY, TOOL_ROUTES } from "@/lib/api";
import { PageHeader, SeverityBadge } from "@/components/Badges";
import { AiTeamPanel } from "@/components/AiTeamPanel";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { t } from "@/lib/i18n";

const Avatar = ({ role }) => (
  <span className="h-8 w-8 rounded-full flex items-center justify-center text-[11px] font-bold shrink-0 border" style={{ background: `${BRANCH_COLORS[role.id]}22`, color: BRANCH_COLORS[role.id], borderColor: BRANCH_COLORS[role.id] }}>{role.initials}</span>
);

const Message = ({ m, roles }) => {
  const role = roles[m.role_id];
  if (!role) return null;
  return (
    <div className="flex gap-3">
      <Avatar role={role} />
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-2 flex-wrap"><span className="text-sm font-medium">{role.person}</span><span className="text-[11px] text-muted-foreground">{role.name}</span>{m.kind && <span className="text-[10px] uppercase tracking-wider px-1.5 rounded bg-secondary text-muted-foreground">{m.kind}</span>}</div>
        <p className="text-sm text-foreground/90 mt-0.5 leading-relaxed">{m.text}</p>
        {(m.nodes?.length > 0 || m.tool) && (
          <div className="flex flex-wrap gap-1.5 mt-1.5">
            {m.nodes?.map((n) => <Link key={n} to={`/?node=${n}`} className="text-[11px] text-muted-foreground hover:text-primary">↗ {n}</Link>)}
            {m.tool && <Link to={TOOL_ROUTES[m.tool]} className="text-[11px] text-primary">{t("Apri strumento →")}</Link>}
          </div>
        )}
      </div>
    </div>
  );
};

export default function TeamPage() {
  const [roles, setRoles] = useState([]);
  const [briefing, setBriefing] = useState([]);
  const [convs, setConvs] = useState([]);
  const [params] = useSearchParams();
  const [roleId, setRoleId] = useState(params.get("role") || "");
  const [q, setQ] = useState("");
  const [openThread, setOpenThread] = useState(null);
  const [busy, setBusy] = useState(false);
  const byId = Object.fromEntries(roles.map((r) => [r.id, r]));

  const load = () => Promise.all([api.get("/team"), api.get("/team/briefing"), api.get("/team/conversations")]).then(([a, b, c]) => { setRoles(a.data); setBriefing(b.data.threads); setConvs(c.data); });
  useEffect(() => { load(); }, []);

  const ask = async (text) => {
    const question = (text || q).trim();
    if (question.length < 3) return;
    setBusy(true);
    try {
      const r = await api.post("/team/ask", { question, role_id: roleId || null });
      setConvs((c) => [r.data, ...c]); setQ("");
    } catch { toast.error(t("Il team non ha potuto rispondere")); } finally { setBusy(false); }
  };

  return (
    <div data-testid="team-page">
      <PageHeader eyebrow={t("Ogni ramo è un ruolo · collaborazione tra nodi")} title={t("Team di direzione")}
        subtitle={t("Otto ruoli, uno per ramo. Ognuno legge i propri dati, propone e coinvolge i colleghi competenti. Puoi interrogarli: risponde chi ha competenza, gli altri integrano.")} />

      <div className="px-5 lg:px-8 py-6 grid xl:grid-cols-[320px_1fr] gap-6">
        <aside className="space-y-2">
          <div className="eyebrow mb-1">{t("Ruoli")}</div>
          {roles.map((r) => (
            <button key={r.id} data-testid={`role-card-${r.id}`} onClick={() => setRoleId(roleId === r.id ? "" : r.id)}
              className={`w-full text-left surface p-3 flex gap-3 transition-colors ${roleId === r.id ? "border-primary" : "hover:border-border/80"}`}>
              <Avatar role={r} />
              <div className="min-w-0">
                <div className="text-sm font-medium">{r.person} · <span className="text-muted-foreground">{r.name}</span></div>
                <div className="text-xs text-muted-foreground line-clamp-2 mt-0.5">{r.mandate}</div>
                <div className="text-[10px] text-muted-foreground mt-1">{r.consolidati}/{r.subs} {t("sotto-rami consolidati")}{r.tools.length ? ` · ${r.tools.length} ${t("strumenti")}` : ""}</div>
              </div>
            </button>
          ))}
        </aside>

        <div className="space-y-6 min-w-0">
          {roles.length > 0 && <AiTeamPanel roles={roles} roleId={roleId} />}
          <section className="surface p-4 lg:p-5">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-heading font-semibold">{t("Risposta rapida a regole (senza AI)")}</h2>
              <span className="text-xs text-muted-foreground">{roleId ? `${t("Destinatario")}: ${byId[roleId]?.name}` : t("Destinatario automatico")}</span>
            </div>
            <Textarea data-testid="team-ask-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("Es. Quanti camerieri servono per 150 ospiti a buffet e quali vini siciliani abbinare?")} className="bg-background min-h-[70px]" />
            <div className="flex flex-wrap gap-1.5 mt-3">
              {(byId[roleId]?.follow_ups || roles.slice(0, 4).map((r) => r.follow_ups[0])).map((f) => (
                <button key={f} data-testid="team-followup-chip" onClick={() => ask(f)} className="text-xs px-2.5 py-1 rounded-full border border-border text-muted-foreground hover:text-primary hover:border-primary">{f}</button>
              ))}
              <Button data-testid="team-ask-submit" size="sm" className="ml-auto" disabled={busy} onClick={() => ask()}><Send size={14} className="mr-1.5" /> {t("Invia")}</Button>
            </div>
            {convs.length > 0 && (
              <div className="mt-5 space-y-4">
                <div className="flex items-center justify-between"><div className="eyebrow">{t("Conversazioni")}</div><button data-testid="team-clear-conversations" onClick={() => api.delete("/team/conversations").then(() => setConvs([]))} className="text-xs text-muted-foreground hover:text-destructive flex items-center gap-1"><Trash2 size={12} /> {t("Svuota")}</button></div>
                {convs.map((c) => (
                  <div key={c.id} data-testid="team-conversation" className="surface-elevated p-4 space-y-3">
                    <div className="text-sm font-medium border-l-2 border-primary pl-3">{c.question}</div>
                    {c.answers.map((a, i) => <Message key={i} m={a} roles={byId} />)}
                  </div>
                ))}
              </div>
            )}
          </section>

          <section>
            <h2 className="font-heading font-semibold mb-3">{t("Riunione di direzione")} · {briefing.length} {t("punti")}</h2>
            <div className="space-y-2">
              {briefing.map((th) => (
                <div key={th.id} data-testid={`thread-${th.id}`} className="surface overflow-hidden" style={{ borderLeft: `3px solid ${SEVERITY[th.severity].color}` }}>
                  <button data-testid={`thread-toggle-${th.id}`} onClick={() => setOpenThread(openThread === th.id ? null : th.id)} className="w-full text-left p-4 flex items-center gap-3">
                    <div className="flex -space-x-2">{[th.owner, ...th.collaborators].map((r) => byId[r] && <Avatar key={r} role={byId[r]} />)}</div>
                    <div className="flex-1 min-w-0"><div className="text-sm font-medium truncate">{th.title}</div><div className="text-xs text-muted-foreground">{t("Guida")} {byId[th.owner]?.name}{th.collaborators.length ? ` · ${t("con")} ${th.collaborators.map((c) => byId[c]?.person).join(", ")}` : ""}</div></div>
                    <SeverityBadge severity={th.severity} />
                  </button>
                  {openThread === th.id && <div className="px-4 pb-4 space-y-3 border-t border-border pt-4">{th.messages.map((m, i) => <Message key={i} m={m} roles={byId} />)}</div>}
                </div>
              ))}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
